import { app, translate } from "../app";
import { translations } from "../translations";
import { languages } from "../locale";
import React, { useRef, useEffect } from "react";
import { CloseOutlined, PinDrop, RightOutlined } from "./icons";
import { trackServiceClick, trackServiceDetailsClick } from "../map-analytics";

interface MapCompanyCardsProps {
  services: Service[];
  maxCards?: number;
  onCardClick?: (service: Service) => void;
  onRemoveCard?: (service: Service) => void;
  onRemoveAll?: () => void;
  selectedServiceId?: number;
  layout?: "horizontal" | "vertical";
}

export function MapCompanyCards({
  services,
  maxCards,
  onCardClick,
  onRemoveCard,
  onRemoveAll,
  selectedServiceId,
  layout = "horizontal",
}: MapCompanyCardsProps) {
  const locale = (languages[app.locale]?.zendesk as string) ?? app.locale;
  const isSwitchboard = app.country === 42;
  const isOrPrograms = app.country === 43;
  const scrollRef = useRef<HTMLDivElement>(null);

  const displayName = (name?: LocalizableText) => {
    if (!name) return "";
    if (isSwitchboard || isOrPrograms) return name["en-US"] ?? "";
    return translate(name);
  };

  const displayAddress = (address?: string) => address ?? "";

  const cardSource = layout === "vertical" ? "chat_card" : "map_card";

  const handleCardActivate = (service: Service) => {
    trackServiceClick({ service, source: cardSource });
    onCardClick?.(service);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  // Filter services with valid locations - show all if maxCards not specified
  const cardServices = maxCards
    ? services.filter((s) => s?.location?.length > 1).slice(0, maxCards)
    : services.filter((s) => s?.location?.length > 1);

  if (cardServices.length === 0) {
    return null;
  }

  const cardContent = (service: Service, isVertical: boolean) => {
    return (
      <>
        {onRemoveCard && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveCard(service);
            }}
            className="absolute top-1.5 right-1.5 z-10 p-1 rounded-full border-none bg-transparent hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Remove card"
          >
            <CloseOutlined className="w-4 h-4 text-gray-400" />
          </button>
        )}
        <div className={isVertical ? "p-4" : "p-3 md:p-2.5"}>
          <h3
            className={`font-bold text-gray-900 line-clamp-2 hyphens-none break-words ${isVertical ? "text-sm mb-2" : "mt-1 text-base mb-1 md:text-sm md:mb-0.5 md:mt-0.5"} ${isSwitchboard || isOrPrograms ? "text-left" : ""}`}
            dir={isSwitchboard || isOrPrograms ? "ltr" : undefined}
            style={isSwitchboard || isOrPrograms ? { direction: "ltr", unicodeBidi: "isolate" } : undefined}
          >
            {displayName(service.name)}
          </h3>
          {service.address && (
            <div className="mb-2">
              <div
                className={`flex items-start gap-1 text-xs text-gray-600 ${isVertical ? "mb-2" : "mb-2 md:text-[11px] md:mb-1.5"} ${isSwitchboard || isOrPrograms ? "text-left" : ""}`}
                dir={isSwitchboard || isOrPrograms ? "ltr" : undefined}
                style={isSwitchboard || isOrPrograms ? { direction: "ltr", unicodeBidi: "isolate" } : undefined}
              >
                <PinDrop
                  className={`flex-shrink-0 mt-0.5 ${isVertical ? "text-xs" : "text-sm md:text-xs"}`}
                />
                <span className="line-clamp-1">{displayAddress(service.address)}</span>
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress(service.address))}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 ${isVertical ? "text-xs" : "text-xs md:text-[11px]"}`}
              >
                {translate(translations.directions)}
                <RightOutlined />
              </a>
            </div>
          )}
          <a
            href={`/${locale.toLowerCase()}/services/${service.id}`}
            onClick={(e) => {
              e.stopPropagation();
              trackServiceDetailsClick({ service, source: cardSource });
            }}
            className={`font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 ${isVertical ? "text-xs" : "text-xs md:text-[11px]"}`}
          >
            {translate(translations.seeMoreDetails)}
            <RightOutlined />
          </a>
        </div>
      </>
    );
  };

  // Vertical layout for AI chat — rendered as a horizontal carousel of cards.
  if (layout === "vertical") {
    const namedServices = cardServices.filter((s) => s.name);
    return (
      <div className="w-full mt-2" style={{ direction: "ltr" }}>
        {onRemoveAll && namedServices.length > 0 && (
          <div className="flex justify-end mb-1">
            <button
              type="button"
              onClick={onRemoveAll}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              aria-label="Remove all cards"
            >
              <CloseOutlined className="w-4 h-4" />
              <span>Remove all</span>
            </button>
          </div>
        )}
        <div className="chat-carousel">
          {namedServices.length > 1 && (
            <button
              type="button"
              className="chat-carousel__arrow chat-carousel__arrow--prev"
              aria-label={translate(translations.previous)}
              onClick={() =>
                scrollRef.current?.scrollBy({ left: -260, behavior: "smooth" })
              }
            >
              <RightOutlined style={{ transform: "rotate(180deg)" }} />
            </button>
          )}
          <div ref={scrollRef} className="chat-carousel__track">
            {namedServices.map((service) => {
              const isSelected = selectedServiceId === service.id;
              return (
                <div
                  key={service.id}
                  role="button"
                  tabIndex={0}
                  aria-label={displayName(service.name)}
                  onClick={() => handleCardActivate(service)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardActivate(service); } }}
                  className={`
                    chat-carousel__card relative bg-white rounded-lg shadow border cursor-pointer
                    transition-all duration-200 hover:shadow-md hover:border-blue-300
                    ${isSelected ? "border-blue-500 shadow" : "border-gray-200"}
                  `}
                >
                  {cardContent(service, true)}
                </div>
              );
            })}
          </div>
          {namedServices.length > 1 && (
            <button
              type="button"
              className="chat-carousel__arrow chat-carousel__arrow--next"
              aria-label={translate(translations.next)}
              onClick={() =>
                scrollRef.current?.scrollBy({ left: 260, behavior: "smooth" })
              }
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Horizontal layout for map carousel (default)
  return (
    <div className="pointer-events-none">
      {/* Mobile-first: smaller padding, scaled down on desktop */}
      <div className="bg-gradient-to-t from-white via-white/95 to-transparent pt-3 pb-2 px-3 md:pt-3 md:pb-1.5 md:px-3">
        {onRemoveAll && cardServices.length > 0 && (
          <div className="flex justify-start mb-1">
            <button
              type="button"
              onClick={onRemoveAll}
              className="pointer-events-auto flex items-center gap-1 text-xs bg-transparent rounded-full p-1 hover:text-gray-700"
              aria-label="Remove all cards"
              style={{
                border:
                  "1px solid rgb(156 163 175 / var(--tw-border-opacity, 1))",
              }}
            >
              <CloseOutlined className="w-4 h-4 text-gray-400 hover:text-gray-700" />
            </button>
          </div>
        )}
        <div
          ref={scrollRef}
          className="overflow-x-auto scrollbar-hide pointer-events-auto"
        >
          <div
            className="flex gap-3 pb-2 md:gap-2.5"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {cardServices
              .filter((s) => s.name)
              .map((service) => {
                const isSelected = selectedServiceId === service.id;
                return (
                  <div
                    key={service.id}
                    role="button"
                    tabIndex={0}
                    aria-label={displayName(service.name)}
                    onClick={() => handleCardActivate(service)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardActivate(service); } }}
                    className={`
                    relative flex-shrink-0 w-[280px] bg-white rounded-lg shadow-lg border-2 cursor-pointer
                    transition-all duration-200 hover:shadow-xl hover:scale-105
                    md:w-52 md:rounded-md md:shadow-md md:border
                    ${isSelected ? "border-blue-500 shadow-xl scale-105" : "border-gray-200"}
                  `}
                  >
                    {cardContent(service, false)}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}